import { useEffect, useMemo, useRef, useState } from "react";
import { computeKit } from "./lib/kit";
import ChatPanel from "./components/ChatPanel";
import DeckDiagram from "./components/DeckDiagram";
import Preview3D, { type Preview3DHandle } from "./components/Preview3D";
import {
  fmtFt,
  perimeter,
  polygonArea,
} from "./lib/geometry";
import type { Design } from "./lib/types";
import type { RailColor, RailHeight } from "./three/buildDeck";
import {
  buildShoppingItems,
  shoppingTotals,
  type ShoppingItem,
} from "./lib/shoppingList";
import { buildDesignSummary } from "./lib/designSummary";
import {
  INSTALL_KIT,
  lookupSku,
  sendCartToViewrail,
} from "./lib/viewrailCart";

type Props = {
  design: Design;
  onChange: (patch: Partial<Design>) => void;
  onReset: () => void;
};

type View = "2d" | "3d";

const HEIGHTS: RailHeight[] = [36, 42];
const COLORS: { id: RailColor; label: string; swatch: string }[] = [
  { id: "black", label: "Black", swatch: "#1a1a1a" },
  { id: "silver", label: "Silver", swatch: "#9aa1a7" },
  { id: "white", label: "White", swatch: "#ececec" },
];

const STAIR_MIN = 4;
const STAIR_MAX = 16;
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 8;

function clampStair(n: number) {
  if (Number.isNaN(n)) return STAIR_MIN;
  return Math.max(STAIR_MIN, Math.min(STAIR_MAX, n));
}

function clampZoom(z: number) {
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));
}

const pillBase =
  "rounded-full border border-neutral-200/80 bg-white/85 shadow-[0_2px_10px_rgba(15,23,42,0.06)] backdrop-blur supports-[backdrop-filter]:bg-white/70";

const sheetBase =
  "border border-neutral-200/80 bg-white/95 shadow-[0_-8px_30px_rgba(15,23,42,0.08)] backdrop-blur supports-[backdrop-filter]:bg-white/85";

const divider = "h-5 w-px shrink-0 bg-neutral-200";

export default function Designer({
  design,
  onChange,
  onReset,
}: Props) {
  const [view, setView] = useState<View>("2d");
  const [zoom2d, setZoom2d] = useState(1);
  const [zoom3d, setZoom3d] = useState(1);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatPulse, setChatPulse] = useState(0);
  const preview3dRef = useRef<Preview3DHandle>(null);

  const handleChatPatch = (patch: Partial<Design>) => {
    onChange(patch);
    setChatPulse((n) => n + 1);
  };

  const { vertices, railHeightInches, color, stairs, railedEdges } = design;

  const perim = perimeter(vertices);
  const area = polygonArea(vertices);
  const kit = computeKit({
    vertices,
    stairs,
    railHeightInches,
    color,
    railedEdges,
  });
  const shoppingItems = useMemo(
    () => buildShoppingItems(kit.postBreakdown, kit.cableFeet),
    [kit.postBreakdown, kit.cableFeet],
  );
  const designSummary = useMemo(
    () => buildDesignSummary(design, kit, shoppingItems),
    [design, kit, shoppingItems],
  );

  const handleDiagramChange = (
    patch: Pick<Partial<Design>, "vertices" | "railedEdges">,
  ) => onChange(patch);

  const zoom = view === "2d" ? zoom2d : zoom3d;
  const zoomIn =
    view === "2d"
      ? () => setZoom2d((z) => clampZoom(z * 1.4))
      : () => preview3dRef.current?.zoomBy(1.4);
  const zoomOut =
    view === "2d"
      ? () => setZoom2d((z) => clampZoom(z / 1.4))
      : () => preview3dRef.current?.zoomBy(1 / 1.4);
  const zoomReset =
    view === "2d"
      ? () => setZoom2d(1)
      : () => preview3dRef.current?.reset();

  const colorMeta = COLORS.find((c) => c.id === color) ?? COLORS[0];

  return (
    <div className="fixed inset-0 isolate z-50 overflow-hidden bg-[var(--color-parchment)] antialiased">
      {/* Full-bleed canvas */}
      <div
        className={`absolute inset-y-0 right-0 transition-[left] duration-200 ${
          chatOpen ? "left-0 sm:left-[360px]" : "left-0"
        }`}
      >
        {view === "2d" ? (
          <DeckDiagram
            vertices={vertices}
            stairs={stairs}
            railedEdges={railedEdges}
            onDesignChange={handleDiagramChange}
            zoom={zoom2d}
            onZoomChange={setZoom2d}
            color={color}
            pulseKey={chatPulse}
          />
        ) : (
          <Preview3D
            ref={preview3dRef}
            vertices={vertices}
            stairs={stairs}
            railHeightInches={railHeightInches}
            color={color}
            railedEdges={railedEdges}
            onZoomChange={setZoom3d}
          />
        )}
      </div>

      {/* Top-center: brand */}
      <div
        className={`pointer-events-none absolute top-3 -translate-x-1/2 flex flex-col items-center gap-1 sm:top-4 transition-[left] duration-200 ${
          chatOpen ? "left-1/2 sm:left-[calc(50%+180px)]" : "left-1/2"
        }`}
      >
        <img src="/viewrail-logo.png" alt="Viewrail" className="h-20 w-auto sm:h-24" />
      </div>

      {/* DESKTOP bottom-center: properties toolbar (sm+ only) */}
      <div
        className={`absolute bottom-5 hidden -translate-x-1/2 flex-col items-center gap-2 sm:flex transition-[left] duration-200 ${
          chatOpen ? "sm:left-[calc(50%+180px)]" : "left-1/2"
        }`}
      >
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-3 rounded-full bg-white/40 px-4 py-1 text-neutral-500 backdrop-blur-sm supports-[backdrop-filter]:bg-white/30">
            <Stat label="Sides" value={String(vertices.length)} />
            <span className="h-4 w-px shrink-0 bg-neutral-300/60" />
            <Stat label="Perim" value={fmtFt(Math.round(perim * 10) / 10)} />
            <span className="h-4 w-px shrink-0 bg-neutral-300/60" />
            <Stat label="Area" value={`${Math.round(area)} sq ft`} />
            <span className="h-4 w-px shrink-0 bg-neutral-300/60" />
            <Stat label="Cable" value={fmtFt(Math.round(kit.cableFeet))} />
          </div>
          <ShoppingList items={shoppingItems} />
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`${pillBase} flex max-w-[calc(100vw-24px)] flex-wrap items-center justify-center gap-x-4 gap-y-2 px-4 py-2`}
          >
            <ViewToggle view={view} onChange={setView} />
            <span className={divider} />
            <ColorPicker value={color} onChange={(c) => onChange({ color: c })} />
            <span className={divider} />
            <HeightToggle
              value={railHeightInches}
              onChange={(h) => onChange({ railHeightInches: h })}
            />
            <span className={divider} />
            <StairsControl
              stairs={stairs}
              edgeCount={vertices.length}
              onChange={(s) => onChange({ stairs: s })}
            />
          </div>
          <ResetButton onReset={onReset} />
        </div>
      </div>

      {/* DESKTOP bottom-right zoom (sm+) */}
      <ZoomPill
        zoom={zoom}
        onZoomOut={zoomOut}
        onZoomReset={zoomReset}
        onZoomIn={zoomIn}
        className="absolute right-5 bottom-5 hidden sm:flex"
      />


      {/* MOBILE bottom sheet with peek summary */}
      <div className="sm:hidden">
        {!sheetOpen && (
          <div className="absolute right-3 bottom-3 left-3 flex flex-col items-center gap-2">
            <div
              className={`${pillBase} flex items-center gap-3 px-4 py-1.5`}
            >
              <Stat label="Sides" value={String(vertices.length)} />
              <span className={divider} />
              <Stat label="Perim" value={fmtFt(Math.round(perim * 10) / 10)} />
              <span className={divider} />
              <Stat label="Area" value={`${Math.round(area)} sq ft`} />
            </div>
            <div
              className={`${pillBase} flex w-full items-center justify-between gap-2 px-3 py-2`}
            >
              <button
                type="button"
                onClick={() => setSheetOpen(true)}
                className="flex flex-1 items-center gap-2 text-left"
                aria-label="Open customize sheet"
              >
                <span className="flex flex-col items-center gap-1">
                  <span className="block h-1 w-8 rounded-full bg-neutral-300" />
                  <span className="block h-1 w-5 rounded-full bg-neutral-200" />
                </span>
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-neutral-700">
                  <span className="font-semibold uppercase tracking-wide text-neutral-900">
                    {view}
                  </span>
                  <span className="text-neutral-300">·</span>
                  <span className="tabular-nums">{railHeightInches}″</span>
                  <span className="text-neutral-300">·</span>
                  <span className="inline-flex items-center gap-1">
                    <ColorDot swatch={colorMeta.swatch} />
                    {colorMeta.label}
                  </span>
                  <span className="text-neutral-300">·</span>
                  <span>Stairs</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setSheetOpen(true)}
                className="rounded-full bg-[var(--color-brand)] px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:opacity-90"
              >
                Customize
              </button>
            </div>
            <ZoomPill
              zoom={zoom}
              onZoomOut={zoomOut}
              onZoomReset={zoomReset}
              onZoomIn={zoomIn}
              className="self-center"
            />
          </div>
        )}

        {sheetOpen && (
          <>
            <button
              type="button"
              aria-label="Close customize sheet"
              onClick={() => setSheetOpen(false)}
              className="absolute inset-0 bg-black/25 backdrop-blur-[2px]"
            />
            <div
              className={`absolute inset-x-0 bottom-0 ${sheetBase} flex max-h-[78vh] flex-col gap-5 overflow-y-auto rounded-t-3xl px-5 pt-3 pb-[max(env(safe-area-inset-bottom),1.25rem)]`}
            >
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                aria-label="Close"
                className="self-center py-1.5"
              >
                <span className="block h-1 w-10 rounded-full bg-neutral-300" />
              </button>

              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-neutral-900">
                  Customize railing
                </h2>
                <button
                  type="button"
                  onClick={onReset}
                  className="rounded-full px-3 py-1 text-xs font-medium text-neutral-500 transition hover:bg-neutral-100"
                >
                  Reset
                </button>
              </div>

              <Section label="View">
                <ViewToggleLarge view={view} onChange={setView} />
              </Section>
              <Section label="Stairs">
                <StairsControlLarge
                  stairs={stairs}
                  edgeCount={vertices.length}
                  onChange={(s) => onChange({ stairs: s })}
                />
              </Section>
              <Section label="Rail height">
                <HeightToggleLarge
                  value={railHeightInches}
                  onChange={(h) => onChange({ railHeightInches: h })}
                />
              </Section>
              <Section label="Color">
                <ColorPickerLarge
                  value={color}
                  onChange={(c) => onChange({ color: c })}
                />
              </Section>

              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="mt-2 rounded-full bg-[var(--color-brand)] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>

      {/* AI design chat — floating bottom-left FAB / panel */}
      <ChatPanel
        design={design}
        designSummary={designSummary}
        onPatch={handleChatPatch}
        open={chatOpen}
        onOpenChange={setChatOpen}
      />
    </div>
  );
}

/* -----------------------------------------------------------
   Shared atoms
   ----------------------------------------------------------- */

function ResetButton({ onReset }: { onReset: () => void }) {
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    if (!confirming) return;
    const t = setTimeout(() => setConfirming(false), 2500);
    return () => clearTimeout(t);
  }, [confirming]);
  return (
    <button
      type="button"
      aria-label="Reset"
      onClick={() => {
        if (confirming) {
          setConfirming(false);
          onReset();
        } else {
          setConfirming(true);
        }
      }}
      className={
        "flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition " +
        (confirming
          ? "bg-red-500 text-white hover:bg-red-600"
          : "text-neutral-600 hover:bg-neutral-100")
      }
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-4"
        aria-hidden="true"
      >
        <path d="M3 12a9 9 0 1 0 3-6.7" />
        <path d="M3 4v5h5" />
      </svg>
      {confirming && <span>Reset?</span>}
    </button>
  );
}

function ShoppingList({ items }: { items: ShoppingItem[] }) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [includeInstallKit, setIncludeInstallKit] = useState(true);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const { qty: totalQty, price: totalPrice } = shoppingTotals(items);
  const fmtUsd = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD" });

  const sendableItems = items.filter((it) => it.qty > 0 && lookupSku(it.name));
  const unmappedItems = items.filter((it) => it.qty > 0 && !lookupSku(it.name));

  async function onSendToViewrail() {
    setSending(true);
    setSendError(null);
    try {
      const payload = sendableItems.map((it) => ({
        name: it.name,
        qty: it.qty,
      }));
      if (includeInstallKit) {
        payload.push({ name: INSTALL_KIT.name, qty: 1 });
      }
      const result = await sendCartToViewrail(payload);
      window.open(result.handoffUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setSendError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        aria-label={`Shopping list, ${totalQty} items, ${fmtUsd(totalPrice)}`}
        className="flex items-center gap-1.5 rounded-full bg-white/60 py-1.5 pl-1.5 pr-2.5 text-neutral-700 backdrop-blur-sm transition hover:bg-white/80 supports-[backdrop-filter]:bg-white/50"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="size-4"
          aria-hidden="true"
        >
          <path d="M3 6h2l2.6 11.2a2 2 0 0 0 2 1.6h7.8a2 2 0 0 0 2-1.5L21 9H6" />
          <circle cx="9" cy="21" r="1" />
          <circle cx="18" cy="21" r="1" />
        </svg>
        <span className="text-xs font-semibold tabular-nums">{totalQty}</span>
        <span className="text-xs font-medium tabular-nums text-neutral-500">
          {fmtUsd(totalPrice)}
        </span>
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Shopping list"
          className="absolute bottom-full right-0 z-30 mb-2 w-[22rem] max-w-[calc(100vw-24px)] rounded-2xl border border-neutral-200/80 bg-white p-3 shadow-[0_10px_30px_rgba(15,23,42,0.12)]"
        >
          {(() => {
            const visible = items.filter((it) => it.qty > 0);
            return (
              <>
                <div className="flex items-center justify-between px-1 pb-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Shopping list
                  </div>
                  <div className="text-[11px] tabular-nums text-neutral-400">
                    {visible.length} item{visible.length === 1 ? "" : "s"}
                  </div>
                </div>
                <ul role="list" className="divide-y divide-neutral-950/5">
                  {visible.map((it) => (
                    <li
                      key={it.name}
                      className="flex items-center justify-between gap-3 px-1 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] font-medium text-neutral-800">
                          {it.name}
                        </div>
                        <div className="text-[11px] tabular-nums text-neutral-500">
                          {it.qty} × {fmtUsd(it.price)}
                        </div>
                      </div>
                      <div className="text-[13px] font-semibold tabular-nums text-neutral-900">
                        {fmtUsd(it.qty * it.price)}
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-1 flex items-center justify-between border-t border-neutral-100 px-1 pt-2 text-xs font-semibold text-neutral-700">
                  <span>Total ({totalQty})</span>
                  <span className="tabular-nums">{fmtUsd(totalPrice)}</span>
                </div>
              </>
            );
          })()}

          <label
            className={
              "mt-3 flex cursor-pointer gap-2 rounded-lg border p-2.5 text-[12px] transition " +
              (includeInstallKit
                ? "border-amber-300 bg-amber-50"
                : "border-neutral-200 bg-neutral-50 hover:border-neutral-300")
            }
          >
            <input
              type="checkbox"
              checked={includeInstallKit}
              onChange={(e) => setIncludeInstallKit(e.target.checked)}
              className="mt-0.5 size-3.5 cursor-pointer accent-amber-500"
            />
            <span className="flex-1 leading-snug">
              <span className="font-semibold text-neutral-800">
                Add Install Kit
              </span>
              <span className="ml-1 tabular-nums text-neutral-500">
                +{fmtUsd(INSTALL_KIT.price)}
              </span>
              <span className="block text-[11px] text-neutral-500">
                {INSTALL_KIT.blurb}
              </span>
            </span>
          </label>

          <div className="mt-3 border-t border-neutral-100 pt-3">
            <button
              type="button"
              onClick={onSendToViewrail}
              disabled={sending || sendableItems.length === 0}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-neutral-900 px-3 py-2.5 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-neutral-800 disabled:cursor-not-allowed disabled:bg-neutral-300"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-4"
                aria-hidden="true"
              >
                <path d="M3 6h2l2.6 11.2a2 2 0 0 0 2 1.6h7.8a2 2 0 0 0 2-1.5L21 9H6" />
                <circle cx="9" cy="21" r="1" />
                <circle cx="18" cy="21" r="1" />
              </svg>
              {sending ? "Sending…" : "Add to Cart"}
            </button>
            {sendError && (
              <p className="mt-2 text-xs text-red-600">{sendError}</p>
            )}
            {unmappedItems.length > 0 && (
              <p className="mt-2 text-[11px] leading-relaxed text-neutral-500">
                Skipped (add manually on viewrail.com):{" "}
                {unmappedItems
                  .map((it) => `${it.qty}× ${it.name}`)
                  .join(", ")}
                .
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="text-xs text-neutral-600">
      <span className="text-neutral-400">{label}</span>{" "}
      <span className="font-medium text-neutral-900 tabular-nums">{value}</span>
    </span>
  );
}

function ColorDot({ swatch, size = 12 }: { swatch: string; size?: number }) {
  return (
    <span
      className="inline-block rounded-full border border-neutral-400/40"
      style={{
        background: swatch,
        width: size,
        height: size,
      }}
    />
  );
}

function ZoomPill({
  zoom,
  onZoomOut,
  onZoomReset,
  onZoomIn,
  className = "",
}: {
  zoom: number;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onZoomIn: () => void;
  className?: string;
}) {
  return (
    <div
      className={`${pillBase} flex items-center text-neutral-700 ${className}`}
    >
      <button
        type="button"
        onClick={onZoomOut}
        aria-label="Zoom out"
        className="grid size-9 place-items-center rounded-l-full text-base transition hover:bg-neutral-100"
      >
        −
      </button>
      <button
        type="button"
        onClick={onZoomReset}
        aria-label="Reset zoom"
        className="border-x border-neutral-200/80 px-3 py-1.5 text-xs font-medium tabular-nums transition hover:bg-neutral-100"
      >
        {Math.round(zoom * 100)}%
      </button>
      <button
        type="button"
        onClick={onZoomIn}
        aria-label="Zoom in"
        className="grid size-9 place-items-center rounded-r-full text-base transition hover:bg-neutral-100"
      >
        +
      </button>
    </div>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
        {label}
      </div>
      {children}
    </div>
  );
}

/* -----------------------------------------------------------
   Compact controls (desktop pill toolbar)
   ----------------------------------------------------------- */

function StairsControl({
  stairs,
  edgeCount,
  onChange,
}: {
  stairs: Design["stairs"];
  edgeCount: number;
  onChange: (s: Design["stairs"]) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const safeEdge = Math.min(stairs.edgeIndex, edgeCount - 1);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative flex items-center text-xs">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={
          "flex items-center gap-1.5 rounded-full px-3 py-1 font-medium transition " +
          (stairs.enabled
            ? "bg-[var(--color-brand)] text-white shadow-sm"
            : "text-neutral-600 hover:bg-neutral-100")
        }
      >
        <span>Stairs</span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="size-3"
          aria-hidden="true"
        >
          <path d="m6 15 6-6 6 6" />
        </svg>
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Stairs settings"
          className="absolute bottom-full left-1/2 z-30 mb-2 w-56 -translate-x-1/2 rounded-2xl border border-neutral-200/80 bg-white p-3 shadow-[0_10px_30px_rgba(15,23,42,0.12)]"
        >
          <label className="flex items-center justify-between gap-2 py-1">
            <span className="text-xs font-medium text-neutral-700">Show Stairs</span>
            <button
              type="button"
              role="switch"
              aria-checked={stairs.enabled}
              onClick={() => onChange({ ...stairs, enabled: !stairs.enabled })}
              className={
                "rounded-full px-3 py-1 text-xs font-medium transition " +
                (stairs.enabled
                  ? "bg-[var(--color-brand)] text-white"
                  : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200")
              }
            >
              {stairs.enabled ? "On" : "Off"}
            </button>
          </label>
          <div
            className={
              "mt-2 flex flex-col gap-2 transition " +
              (stairs.enabled ? "" : "pointer-events-none opacity-50")
            }
          >
            <label className="flex items-center justify-between gap-2">
              <span className="text-xs text-neutral-600">Length</span>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={4}
                  max={16}
                  step={0.5}
                  value={stairs.lengthFt}
                  aria-label="Stair length in feet"
                  onChange={(e) =>
                    onChange({ ...stairs, lengthFt: clampStair(Number(e.target.value)) })
                  }
                  className="w-16 rounded-full border border-neutral-200 bg-white px-2 py-1 text-xs tabular-nums focus:border-[var(--color-brand)] focus:outline-none"
                />
                <span className="text-neutral-400">ft</span>
              </div>
            </label>
            <label className="flex items-center justify-between gap-2">
              <span className="text-xs text-neutral-600">Side</span>
              <select
                value={safeEdge}
                aria-label="Stair edge"
                onChange={(e) =>
                  onChange({ ...stairs, edgeIndex: Number(e.target.value) })
                }
                className="rounded-full border border-neutral-200 bg-white px-2 py-1 text-xs focus:border-[var(--color-brand)] focus:outline-none"
              >
                {Array.from({ length: edgeCount }, (_, i) => (
                  <option key={i} value={i}>
                    Side {i + 1}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center justify-between gap-2">
              <span className="text-xs text-neutral-600">Railing</span>
              <button
                type="button"
                role="switch"
                aria-checked={stairs.withRailing}
                onClick={() =>
                  onChange({ ...stairs, withRailing: !stairs.withRailing })
                }
                className={
                  "rounded-full px-3 py-1 text-xs font-medium transition " +
                  (stairs.withRailing
                    ? "bg-[var(--color-brand)] text-white"
                    : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200")
                }
              >
                {stairs.withRailing ? "On" : "Off"}
              </button>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

function HeightToggle({
  value,
  onChange,
}: {
  value: RailHeight;
  onChange: (h: RailHeight) => void;
}) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <div
        role="radiogroup"
        aria-label="Rail height"
        className="inline-flex overflow-hidden rounded-full border border-neutral-200 bg-white"
      >
        {HEIGHTS.map((h) => {
          const active = value === h;
          return (
            <button
              key={h}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(h)}
              className={
                "px-3 py-1 font-medium tabular-nums transition " +
                (active
                  ? "bg-[var(--color-brand)] text-white"
                  : "text-neutral-600 hover:bg-neutral-50")
              }
            >
              {h}″
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ColorPicker({
  value,
  onChange,
}: {
  value: RailColor;
  onChange: (c: RailColor) => void;
}) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <div
        role="radiogroup"
        aria-label="Railing color"
        className="inline-flex items-center gap-1.5"
      >
        {COLORS.map((c) => {
          const active = value === c.id;
          return (
            <button
              key={c.id}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={c.label}
              title={c.label}
              onClick={() => onChange(c.id)}
              className={
                "grid size-6 place-items-center rounded-full border-2 transition " +
                (active
                  ? "border-[var(--color-brand)]"
                  : "border-neutral-300 hover:border-neutral-500")
              }
            >
              <span
                className="block size-3.5 rounded-full border border-neutral-400/40"
                style={{ background: c.swatch }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: View;
  onChange: (v: View) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="View"
      className="inline-flex overflow-hidden rounded-full border border-neutral-200 bg-white text-xs"
    >
      {(["2d", "3d"] as const).map((v) => {
        const active = view === v;
        return (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(v)}
            className={
              "px-3 py-1 font-medium tracking-wide uppercase transition " +
              (active
                ? "bg-[var(--color-brand)] text-white"
                : "text-neutral-600 hover:bg-neutral-50")
            }
          >
            {v}
          </button>
        );
      })}
    </div>
  );
}

/* -----------------------------------------------------------
   Larger labelled controls (mobile bottom sheet)
   ----------------------------------------------------------- */

function HeightToggleLarge({
  value,
  onChange,
}: {
  value: RailHeight;
  onChange: (h: RailHeight) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Rail height"
      className="grid grid-cols-2 gap-2"
    >
      {HEIGHTS.map((h) => {
        const active = value === h;
        return (
          <button
            key={h}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(h)}
            className={
              "rounded-2xl border px-4 py-3 text-base font-semibold tabular-nums transition " +
              (active
                ? "border-[var(--color-brand)] bg-[var(--color-brand)] text-white shadow-sm"
                : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300")
            }
          >
            {h}″
          </button>
        );
      })}
    </div>
  );
}

function ColorPickerLarge({
  value,
  onChange,
}: {
  value: RailColor;
  onChange: (c: RailColor) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Railing color"
      className="grid grid-cols-3 gap-2"
    >
      {COLORS.map((c) => {
        const active = value === c.id;
        return (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(c.id)}
            className={
              "flex flex-col items-center gap-2 rounded-2xl border px-2 py-3 text-xs font-medium transition " +
              (active
                ? "border-[var(--color-brand)] bg-[var(--color-brand)]/5 text-neutral-900"
                : "border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300")
            }
          >
            <span
              className="block size-7 rounded-full border border-neutral-400/40 shadow-inner"
              style={{ background: c.swatch }}
            />
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

function ViewToggleLarge({
  view,
  onChange,
}: {
  view: View;
  onChange: (v: View) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="View"
      className="grid grid-cols-2 gap-2"
    >
      {(["2d", "3d"] as const).map((v) => {
        const active = view === v;
        return (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(v)}
            className={
              "rounded-2xl border px-4 py-3 text-base font-semibold tracking-wide uppercase transition " +
              (active
                ? "border-[var(--color-brand)] bg-[var(--color-brand)] text-white shadow-sm"
                : "border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300")
            }
          >
            {v}
          </button>
        );
      })}
    </div>
  );
}

function StairsControlLarge({
  stairs,
  edgeCount,
  onChange,
}: {
  stairs: Design["stairs"];
  edgeCount: number;
  onChange: (s: Design["stairs"]) => void;
}) {
  const safeEdge = Math.min(stairs.edgeIndex, edgeCount - 1);
  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={stairs.enabled}
        onClick={() => onChange({ ...stairs, enabled: !stairs.enabled })}
        className={
          "flex items-center justify-between rounded-2xl border px-4 py-3 text-sm font-medium transition " +
          (stairs.enabled
            ? "border-[var(--color-brand)] bg-[var(--color-brand)] text-white"
            : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300")
        }
      >
        <span>Stairs</span>
        <span
          className={
            "rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide " +
            (stairs.enabled
              ? "bg-white/20 text-white"
              : "bg-neutral-100 text-neutral-500")
          }
        >
          {stairs.enabled ? "On" : "Off"}
        </span>
      </button>
      {stairs.enabled && (
        <div className="grid grid-cols-2 gap-2">
          <label className="flex items-center gap-2 rounded-2xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-700">
            <span className="text-xs text-neutral-500">Length</span>
            <input
              type="number"
              min={4}
              max={16}
              step={0.5}
              value={stairs.lengthFt}
              aria-label="Stair length in feet"
              onChange={(e) =>
                onChange({ ...stairs, lengthFt: clampStair(Number(e.target.value)) })
              }
              className="w-full bg-transparent text-right text-sm font-medium tabular-nums focus:outline-none"
            />
            <span className="text-xs text-neutral-400">ft</span>
          </label>
          <label className="flex items-center gap-2 rounded-2xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-700">
            <span className="text-xs text-neutral-500">Edge</span>
            <select
              value={safeEdge}
              aria-label="Stair edge"
              onChange={(e) =>
                onChange({ ...stairs, edgeIndex: Number(e.target.value) })
              }
              className="w-full bg-transparent text-right text-sm font-medium focus:outline-none"
            >
              {Array.from({ length: edgeCount }, (_, i) => (
                <option key={i} value={i}>
                  Side {i + 1}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            role="switch"
            aria-checked={stairs.withRailing}
            onClick={() =>
              onChange({ ...stairs, withRailing: !stairs.withRailing })
            }
            className={
              "col-span-2 flex items-center justify-between rounded-2xl border px-4 py-3 text-sm font-medium transition " +
              (stairs.withRailing
                ? "border-[var(--color-brand)] bg-[var(--color-brand)] text-white"
                : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300")
            }
          >
            <span>Railing on stairs</span>
            <span
              className={
                "rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide " +
                (stairs.withRailing
                  ? "bg-white/20 text-white"
                  : "bg-neutral-100 text-neutral-500")
              }
            >
              {stairs.withRailing ? "On" : "Off"}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
